import { useEffect, useRef } from 'react';

import { track } from './client';
import { setTelemetryContext } from './context';

export interface ModalTelemetryState {
  isOpen: boolean;
  modal?: string;
  orderPid?: string | number;
  orderStatus?: string;
  /** How the order panel came up: a row click, or the `?order=` / `?tb=` deep link. */
  via: 'row' | 'url';
}

const ORDER_MODALS = ['order', 'thunderbird-order'];

interface OpenModal {
  modal: string;
  orderPid?: string;
}

// Which panel is up (for the envelope), plus modal_opened/closed and order_opened as it changes.
// Switching straight from one order row to another closes the first and opens the second.
export function useModalTelemetry({
  isOpen,
  modal,
  orderPid,
  orderStatus,
  via,
}: ModalTelemetryState): void {
  const current = useRef<OpenModal | null>(null);
  const pid =
    orderPid == null || orderPid === '' ? undefined : String(orderPid);

  useEffect(() => {
    const next: OpenModal | null =
      isOpen && modal ? { modal, orderPid: pid } : null;
    const previous = current.current;
    if (
      previous?.modal === next?.modal &&
      previous?.orderPid === next?.orderPid
    )
      return;

    if (previous) {
      track('modal_closed', {
        modal: previous.modal,
        orderPid: previous.orderPid,
      });
    }
    if (next) {
      track('modal_opened', { modal: next.modal, orderPid: next.orderPid });
      if (next.orderPid && ORDER_MODALS.includes(next.modal)) {
        track('order_opened', {
          pid: next.orderPid,
          status: orderStatus,
          kind: next.modal,
          via,
        });
      }
    }
    current.current = next;
    setTelemetryContext({ modal: next?.modal, orderPid: next?.orderPid });
    // Status and `via` describe the open; only an open/close/switch is a change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modal, pid]);
}
